import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Code2, Lock, Github, Package, Cpu, Copy, Check, Terminal as TerminalIcon, Rocket, Shield } from 'lucide-react';

const REPO_URL = 'https://github.com/pumpfunsniper-private/core';

const SNIPPETS = [
  {
    title: 'Architecture overview',
    lang: 'bash',
    code: `pumpfun-sniper/
├── apps/
│   ├── web/              # React 19 main site (this one)
│   ├── terminal/         # Vanilla JS trading terminal
│   └── api/              # FastAPI — subscription & webhook service
├── services/
│   ├── sniper-core/      # Rust execution engine (sub-10ms)
│   ├── scanner/          # Go WebSocket fan-out from pump.fun
│   ├── rugpull-ml/       # Python — PyTorch rugpull classifier v3
│   └── agent-router/     # Node — Gemini/GPT/Claude/Grok orchestration
├── infra/
│   ├── terraform/        # AWS eu-central-1 + Hetzner bare metal RPCs
│   └── helm/             # Kubernetes charts (k3s)
└── packages/
    ├── sdk-js/           # Public TypeScript SDK
    └── sdk-py/           # Public Python SDK`,
  },
  {
    title: 'Clone the private repo',
    lang: 'bash',
    code: `# 1. SSH-add the invite key we email you on first PRO payment
chmod 600 ~/.ssh/pfs_deploy_key
ssh-add ~/.ssh/pfs_deploy_key

# 2. Clone the monorepo
git clone git@github.com:pumpfunsniper-private/core.git
cd core

# 3. Bootstrap
pnpm install
cp .env.example .env
pnpm dev`,
  },
  {
    title: 'Run the local terminal',
    lang: 'bash',
    code: `# Terminal only (vanilla JS, no React)
pnpm --filter terminal dev
# → http://localhost:5174/terminal/

# Full stack (web + api + scanner + terminal)
pnpm dev:all`,
  },
  {
    title: 'Deploy to your own VPS',
    lang: 'bash',
    code: `# One-liner install on a fresh Ubuntu 24.04 box
curl -sSL https://deploy.pumpfunsniper.io/install.sh | sudo bash -s -- \\
  --solana-rpc=https://rpc.helius.xyz/?api-key=YOUR_KEY \\
  --license-key=$PFS_PRO_LICENSE

# That's it. The installer wires nginx, k3s, the Rust binary and systemd.`,
  },
  {
    title: 'Use the public SDK (open-source)',
    lang: 'ts',
    code: `import { Sniper } from '@pumpfunsniper/sdk';

const sniper = new Sniper({
  rpcUrl: process.env.SOLANA_RPC!,
  wallet: process.env.PRIVATE_KEY!,    // never log this
  filters: {
    minLiquidityUsd: 20_000,
    devHoldMax: 0.15,
    rugShield: 'v3',
  },
});

sniper.on('snipe', (t) => {
  console.log(\`✓ sniped \${t.symbol} for \${t.costSol} SOL\`);
});

await sniper.start();`,
  },
];

function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative group">
      <button
        onClick={copy}
        className="absolute top-3 right-3 p-2 rounded-lg bg-background/80 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]/60 transition-all text-xs"
        aria-label="Copy"
      >
        {copied ? <Check className="w-4 h-4 text-[hsl(var(--primary))]" /> : <Copy className="w-4 h-4" />}
      </button>
      <pre className="p-4 sm:p-5 rounded-xl bg-black/60 border border-border/60 font-mono text-[12.5px] text-[hsl(var(--primary))] overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function LockedWall() {
  return (
    <div className="relative">
      <div className="glass-card p-10 sm:p-14 text-center overflow-hidden" data-testid="source-code-locked">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-[hsl(var(--primary))]/15 blur-[80px]" />
        <div className="relative">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/40 flex items-center justify-center shadow-[0_0_40px_rgba(52,200,120,0.25)]">
            <Lock className="w-9 h-9 text-[hsl(var(--primary))]" />
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-6">
            Source code is <span className="gradient-text">PRO+</span>
          </h2>
          <p className="text-muted-foreground mt-3 max-w-md mx-auto leading-relaxed">
            The full monorepo — Rust execution engine, rugpull ML model,
            terminal codebase, deploy scripts — is available to Pro and Whale subscribers.
            Includes a private GitHub invite and a 1-line self-host installer.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <a href="#/pricing" className="btn-primary" data-testid="source-unlock-btn">
              <Rocket className="w-4 h-4" /> Unlock with Pro
            </a>
            <a href="#/contact" className="btn-secondary">
              Ask a question
            </a>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-10 text-left">
            {[
              { Icon: Package, t: 'Full monorepo', d: 'Web, API, terminal, Rust core, ML models.' },
              { Icon: TerminalIcon, t: '1-line self-host', d: 'curl + systemd on Ubuntu 24.04.' },
              { Icon: Shield, t: 'Signed commits', d: 'Every release signed by two maintainers.' },
            ].map((f) => (
              <div key={f.t} className="p-4 rounded-xl border border-border bg-background/40">
                <f.Icon className="w-5 h-5 text-[hsl(var(--primary))]" />
                <div className="font-display font-bold text-sm mt-3">{f.t}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function UnlockedView() {
  return (
    <div className="space-y-6">
      <div className="glass-card p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="source-code-unlocked">
        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/40 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
          <Github className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg">Private repository</div>
          <a href={REPO_URL} className="font-mono text-sm text-[hsl(var(--primary))] break-all hover:underline">{REPO_URL}</a>
        </div>
        <a href={REPO_URL} className="btn-primary w-full sm:w-auto">Open repo</a>
      </div>
      {SNIPPETS.map((s, i) => (
        <motion.div
          key={s.title}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: i * 0.05 }}
          className="glass-card p-5 sm:p-6"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-[hsl(var(--primary))]" />
              <h3 className="font-display font-bold text-base">{s.title}</h3>
            </div>
            <span className="px-2 py-1 rounded-md font-mono text-[10px] uppercase tracking-wider text-muted-foreground bg-background/40 border border-border">{s.lang}</span>
          </div>
          <CodeBlock lang={s.lang} code={s.code} />
        </motion.div>
      ))}
    </div>
  );
}

export default function SourceCode() {
  
  const [unlocked, setUnlocked] = useState(false);

  return (
    <div className="pt-12 sm:pt-16 pb-16 sm:pb-20 container-x" data-testid="source-code-page">
      <section className="text-center py-10">
        <span className="chip"><Code2 className="w-3 h-3" /> Source Code</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">
          Read the <span className="gradient-text">whole stack</span>
        </h1>
        <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
          Full source of the web, terminal, Rust execution core and ML models — available to Pro &amp; Whale subscribers.
        </p>
        <div className="inline-flex items-center gap-2 mt-6 p-1 rounded-xl border border-border bg-background/40 backdrop-blur-xl">
          {[
            { k: false, label: 'Locked preview' },
            { k: true, label: 'PRO preview' },
          ].map((t) => (
            <button
              key={String(t.k)}
              onClick={() => setUnlocked(t.k)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                unlocked === t.k ? 'bg-[hsl(var(--primary))] text-black' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`source-toggle-${t.k ? 'pro' : 'locked'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] font-mono text-muted-foreground mt-3">Demo toggle — real access is wallet-gated on subscription.</p>
      </section>

      <div className="mt-6">
        {unlocked ? <UnlockedView /> : <LockedWall />}
      </div>
    </div>
  );
}
